import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

type DecimalString = string | null;

type IsoDateString = string | null;

export interface ReconciliationByTradeRow {
  contractReference: string | null;
  asset: string | null;
  internalNet: DecimalString;
  internalBrokerActionNet: DecimalString;
  internalPriceFixNet: DecimalString;
  brokerLong: DecimalString;
  brokerShort: DecimalString;
  brokerNet: DecimalString;
  brokerStatus: string | null;
  brokerLastSeen: IsoDateString;
  matchStatus: string | null;
  hasUnwind: boolean | null;
  execChain: string[] | null;
  firstExec: IsoDateString;
  lastExec: IsoDateString;
  currencyPair: string | null;
  // 'all' — deliberate all-accounts comparison; present only while the LHA flag is on.
  accountScope?: string | null;
}

export interface ReconciliationByTradeFilters {
  contractReference?: string;
  matchStatus?: string;
}

export class HedgingReconciliationByTradeApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingReconciliationByTradeApiError';
  }
}

function buildQueryString(filters: ReconciliationByTradeFilters): string {
  const params = new URLSearchParams();
  if (filters.contractReference) params.set('contractReference', filters.contractReference);
  if (filters.matchStatus) params.set('matchStatus', filters.matchStatus);
  const s = params.toString();
  return s.length === 0 ? '' : `?${s}`;
}

async function fetchReconciliationByTrade(
  filters: ReconciliationByTradeFilters,
): Promise<ReconciliationByTradeRow[]> {
  const response = await authFetch(
    `${getApiBaseUrl()}/hedging/reconciliation/by-trade${buildQueryString(filters)}`,
  );
  if (!response.ok) {
    throw new HedgingReconciliationByTradeApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as ReconciliationByTradeRow[];
}

export const HEDGING_RECONCILIATION_BY_TRADE_QUERY_KEY = [
  'hedging',
  'reconciliation-by-trade',
] as const;

export function useReconciliationByTrade(filters: ReconciliationByTradeFilters = {}) {
  return useQuery({
    queryKey: [...HEDGING_RECONCILIATION_BY_TRADE_QUERY_KEY, filters],
    queryFn: () => fetchReconciliationByTrade(filters),
  });
}
